import { useEffect, useMemo, useState } from 'react';
import { usePagedReader } from '../../hooks/usePagedReader';
import { SermonBlocks, type PreachBlock } from './SermonBlocks';
import type { SectionStyle } from '../../hooks/useSectionStyles';

/**
 * Book mode's paged viewport — the DOM shape from docs/plan_preach_book_mode.md §2.1:
 *
 *   probe        fixed, invisible; the ResizeObserver target (§2.4)
 *   stage        fixed below the bars; height pixel-locked by the hook
 *   └ width box  same margin math as scroll mode — margins drive column width
 *     └ viewport overflow-x scroller with mandatory snap + edge-zone tap handler
 *       ├ flow   the multicol fragmentainer (prose + book-flow), ends in the sentinel
 *       └ snap   page-sized snap areas — column boxes are anonymous and cannot
 *                take scroll-snap-align themselves
 *   plus stage overlays: folio button (+ jump list), coach mark, sr-only live region
 *
 * Navigation (§4.2): ~25% invisible edge zones — pulpit reality is arm's length,
 * adrenaline, often one-handed; 12% is a leisure-reading number. Center tap toggles
 * the chrome. No hover chevrons: pointer-fine contexts already have click and
 * keyboard; preaching happens on touch.
 */

const MIN_COL_CH = 24; // safety floor (§ "Why manual columns"): below this, 2 → 1
const EDGE_ZONE = 0.25;
// The coach mark retires once the invisible UI is demonstrably learned (first
// successful center-tap) or after three showings — not on first display: chrome
// auto-hides after 4s, and a user who never learned the reveal gesture is locked
// out of the controls with no affordance at all.
const COACH_DONE_KEY = 'preach_bookCoachDone';
const COACH_VIEWS_KEY = 'preach_bookCoachViews';

interface PreachTheme {
  bg: string;
  text: string;
  subText: string;
  controlsBg: string;
  border: string;
}

interface BookReaderProps {
  blocks: PreachBlock[];
  styles: SectionStyle[];
  readingMode: 'light' | 'warm' | 'dark';
  columns: 1 | 2;
  fontSize: number;
  fontFamily: 'sans' | 'serif';
  /** Height of the ALWAYS-VISIBLE info bar (title / timer / progress). The stage
   *  sits below it; only the controls row above it hides, overlaying the stage's
   *  top transiently while revealed. */
  topOffset: number;
  /** Content width/maxWidth strings from PreachMode's margin math. */
  width: string;
  maxWidth: string;
  theme: PreachTheme;
  /** Epoch ms when the sermon was opened — the folio's elapsed clock counts from
   *  here so a mode switch mid-sermon doesn't reset it. */
  startedAt: number;
  /** Center-tap toggles the chrome (§4.3). Edge taps and swipes deliberately do
   *  NOT touch it — a page turn that flashes the bars every 60–90s for 40 minutes
   *  is the exact failure §4.3 calls out. */
  onToggleChrome?: () => void;
  /** Reports page position up so PreachMode can drive the progress ring and the
   *  `Page N of M` label in the info bar (§4.3). */
  onPageState?: (pageIndex: number, pageCount: number) => void;
  /**
   * Called on every user-initiated page turn. Wired to `useWakeLock().retry` —
   * free insurance against system-initiated wake-lock releases mid-sermon
   * (spike verdict 6): each turn re-requests inside a user gesture, which is
   * exactly the context some WebKit builds require.
   */
  onUserNavigate?: () => void;
}

/** Isolated so the 1s tick re-renders this leaf only, never the paginated flow.
 *  Elapsed is computed inside timers, keeping render pure. */
function FolioClock({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const compute = () =>
      setElapsed(startedAt > 0 ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0);
    const kick = window.setTimeout(compute, 0);
    const t = window.setInterval(compute, 1000);
    return () => {
      window.clearTimeout(kick);
      window.clearInterval(t);
    };
  }, [startedAt]);
  return <>{Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}</>;
}

export function BookReader({
  blocks,
  styles,
  readingMode,
  columns,
  fontSize,
  fontFamily,
  topOffset,
  width,
  maxWidth,
  theme,
  startedAt,
  onToggleChrome,
  onPageState,
  onUserNavigate,
}: BookReaderProps) {
  const {
    viewportRef,
    flowRef,
    sentinelRef,
    probeRef,
    pageIndex,
    pageCount,
    stride,
    gap,
    stageHeight,
    ready,
    goTo,
    goToBlock,
    next,
    prev,
    flowStyle,
  } = usePagedReader({
    columns,
    // ~24ch at the current font: 1ch ≈ 0.5em, 1em = 16px × fontSize%. Approximate on
    // purpose — this is a floor against unreadable slivers, not typography.
    minColumnWidth: Math.round(MIN_COL_CH * 8 * (fontSize / 100)),
    // Any user setting that moves text metrics repaginates (with position restore).
    remeasureToken: `${fontSize}|${fontFamily}|${width}|${maxWidth}|${topOffset}`,
  });

  const [showJumpList, setShowJumpList] = useState(false);
  // Coach mark (§4.4): every affordance here is invisible on touch.
  const [showCoach, setShowCoach] = useState(() => localStorage.getItem(COACH_DONE_KEY) !== '1');

  useEffect(() => {
    onPageState?.(pageIndex, pageCount);
  }, [pageIndex, pageCount, onPageState]);

  useEffect(() => {
    if (!showCoach) return;
    const views = Number(localStorage.getItem(COACH_VIEWS_KEY) || '0') + 1;
    localStorage.setItem(COACH_VIEWS_KEY, String(views));
    if (views >= 3) localStorage.setItem(COACH_DONE_KEY, '1');
    // Matches the .book-coach fade-out animation length.
    const t = window.setTimeout(() => setShowCoach(false), 4500);
    return () => window.clearTimeout(t);
  }, [showCoach]);

  // The folio jump list (§4.1): "get me to Point 3" is the highest-value recovery
  // action when someone loses their place live, and the paginated payoff scroll
  // mode cannot offer. DOMParser, not a live div: the block HTML is unsanitized
  // here and a parser document is inert (no resource loads, no handlers).
  const mainPoints = useMemo(() => {
    const parser = new DOMParser();
    return blocks
      .map((block, blockIndex) => ({ block, blockIndex }))
      .filter(({ block }) => block.type === 'section' && block.sectionType === 'main-point')
      .map(({ block, blockIndex }) => {
        const text = (parser.parseFromString(block.content, 'text/html').body.textContent || '')
          .trim()
          .replace(/\s+/g, ' ');
        return {
          blockIndex,
          pointNumber: block.pointNumber || '',
          label: text.length > 60 ? `${text.slice(0, 60)}…` : text,
        };
      });
  }, [blocks]);

  // Edge zones (§4.2). A click handler with target guards, NOT overlay divs — an
  // overlay would swallow the tap a scripture link needs to receive. Attached
  // NATIVELY, not via React's onClick: iOS Safari does not reliably synthesize
  // click events for taps on non-interactive elements when the listener is
  // delegated to an ancestor — and this handler IS the page-turn UI on touch.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onClick = (e: MouseEvent) => {
      if (showJumpList) {
        setShowJumpList(false);
        return;
      }
      if ((e.target as Element).closest('a, button')) return;
      // A tap that finishes a text selection must not turn the page.
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return;
      if (showCoach) setShowCoach(false);
      const rect = vp.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      if (frac < EDGE_ZONE) {
        onUserNavigate?.();
        prev();
      } else if (frac > 1 - EDGE_ZONE) {
        onUserNavigate?.();
        next();
      } else {
        // The invisible UI is demonstrably learned — retire the coach mark.
        localStorage.setItem(COACH_DONE_KEY, '1');
        onToggleChrome?.();
      }
    };
    vp.addEventListener('click', onClick);
    return () => vp.removeEventListener('click', onClick);
  }, [viewportRef, showJumpList, showCoach, next, prev, onToggleChrome, onUserNavigate]);

  // Keyboard page turning (§4.2).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'PageDown':
          e.preventDefault();
          onUserNavigate?.();
          next();
          break;
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          onUserNavigate?.();
          prev();
          break;
        case ' ':
          e.preventDefault();
          onUserNavigate?.();
          if (e.shiftKey) prev();
          else next();
          break;
        case 'Home':
          e.preventDefault();
          goTo(0);
          break;
        case 'End':
          e.preventDefault();
          goTo(pageCount - 1);
          break;
        case 'Escape':
          setShowJumpList(false);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, goTo, pageCount, onUserNavigate]);

  // Swipe turns never pass through a JS handler (native scroll), so re-request the
  // wake lock on touchend instead — still inside the gesture's transient activation.
  // retry() no-ops while the lock is held, so this is free.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || !onUserNavigate) return;
    const onTouchEnd = () => onUserNavigate();
    vp.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => vp.removeEventListener('touchend', onTouchEnd);
  }, [viewportRef, onUserNavigate]);

  return (
    <>
      {/* The observed probe — a separate element so the observer never watches
          something this feature mutates (§2.4). Spans the stage's target area,
          so its rect IS the stage geometry, arithmetic-free. */}
      <div
        ref={probeRef}
        className="book-probe"
        aria-hidden
        style={{
          position: 'fixed',
          top: topOffset,
          bottom: 0,
          left: 0,
          right: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      />

      {/* The stage sits below the ALWAYS-VISIBLE info bar. The §2.4 invariant
          forbids resizing the page area on chrome toggle, so when the controls row
          is revealed, it (and the info bar it pushes down) transiently OVERLAYS the
          stage's top rather than displacing it; hiding it re-aligns the info bar
          flush above the page with no dead strip. */}
      <div
        className="book-stage"
        style={{
          position: 'fixed',
          top: topOffset,
          left: 0,
          right: 0,
          // Pixel-locked once measured, so sub-40px viewport jitter the observer
          // ignores cannot resize the fragmentainer and reflow mid-sentence (§2.4).
          // Constant across chrome hide/show for the same reason — hidden bars
          // leave headroom, never a repaginated page.
          ...(stageHeight > 0 ? { height: `${stageHeight}px` } : { bottom: 0 }),
          // Hidden until fonts/images settle and the first measure lands — earlier
          // paints show a layout that is still going to shift.
          visibility: ready ? 'visible' : 'hidden',
        }}
      >
        <div className="mx-auto h-full" style={{ width, maxWidth }}>
          <div
            ref={viewportRef}
            className="book-viewport"
            style={{
              position: 'relative',
              height: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollSnapType: 'x mandatory',
              // 'none', not 'contain': also kills the scroller's own rubber-band so
              // the document behind the pager never peeks through (§5.4).
              overscrollBehavior: 'none',
              // Bottom padding doubles as the folio's quiet strip.
              padding: '1.5rem 1.25rem 2.5rem',
            }}
          >
            <div
              ref={flowRef}
              className="book-flow prose prose-lg max-w-none"
              style={{
                ...flowStyle,
                '--base-font-size': `${fontSize}%`,
                fontSize: 'var(--base-font-size)',
                fontFamily:
                  fontFamily === 'serif' ? 'Georgia, "Times New Roman", serif' : 'inherit',
              } as React.CSSProperties}
            >
              <SermonBlocks
                blocks={blocks}
                variant="page"
                styles={styles}
                readingMode={readingMode}
              />
              <span
                ref={sentinelRef}
                data-page-sentinel
                style={{ display: 'inline-block', width: 0, height: 0 }}
              />
            </div>

            {/* Page-sized snap areas, not 1px points — on a real iPad, momentum
                flicks settled between 1px targets (spike verdict 5). Positioned in
                the viewport's padding box so `left: i * stride` lands column i at
                the same visual inset page 0 has. */}
            <div className="book-snap" aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {stride > 0 &&
                Array.from({ length: pageCount }, (_, i) => (
                  <i
                    key={i}
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: i * stride,
                      width: Math.max(1, stride - gap),
                      scrollSnapAlign: 'start',
                      scrollSnapStop: 'always',
                    }}
                  />
                ))}
            </div>
          </div>
        </div>

        {/* Jump list — a quiet panel above the folio. */}
        {showJumpList && mainPoints.length > 0 && (
          <div
            role="menu"
            aria-label="Jump to main point"
            className="book-jump"
            style={{
              position: 'absolute',
              bottom: '2.5rem',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              minWidth: '14rem',
              maxWidth: 'min(22rem, 92%)',
              maxHeight: '55%',
              overflowY: 'auto',
              background: theme.controlsBg,
              border: `1px solid ${theme.border}`,
              borderRadius: '0.75rem',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              backdropFilter: 'blur(10px)',
              padding: '0.25rem 0',
            }}
          >
            {mainPoints.map(point => (
              <button
                key={point.blockIndex}
                role="menuitem"
                type="button"
                onClick={() => {
                  setShowJumpList(false);
                  onUserNavigate?.();
                  goToBlock(point.blockIndex);
                }}
                className="flex w-full items-baseline gap-2.5 px-4 py-2 text-left text-sm transition-opacity hover:opacity-70"
                style={{ color: theme.text }}
              >
                <span className="book-point-number" style={{ fontSize: '1.15rem' }}>
                  {point.pointNumber || '•'}
                </span>
                <span className="truncate">{point.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* One-time coach mark (§4.4) — without it, a first-time user taps center,
            watches the bars vanish, and concludes the app is broken. */}
        {ready && showCoach && (
          <div
            className="book-coach"
            style={{
              position: 'absolute',
              bottom: '3.25rem',
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: '92%',
              textAlign: 'center',
              background: theme.controlsBg,
              color: theme.subText,
              border: `1px solid ${theme.border}`,
              borderRadius: '9999px',
              padding: '0.4rem 1rem',
              fontSize: '0.8rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
              pointerEvents: 'none',
            }}
          >
            Swipe or tap an edge to turn · tap center for controls
          </div>
        )}

        {/* Folio (§4.1) — the one always-visible element: page + elapsed, ~40%
            opacity, present even when chrome is hidden. A button when there are
            main points to jump to; inert otherwise. */}
        {mainPoints.length > 0 ? (
          <button
            type="button"
            className="book-folio"
            onClick={() => setShowJumpList(s => !s)}
            title="Jump to a main point"
            style={{
              position: 'absolute',
              bottom: '0.35rem',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '0.15rem 0.75rem',
              fontSize: '0.75rem',
              fontVariantNumeric: 'tabular-nums',
              opacity: 0.4,
              color: theme.subText,
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {pageIndex + 1} / {pageCount} · <FolioClock startedAt={startedAt} />
          </button>
        ) : (
          <div
            className="book-folio"
            style={{
              position: 'absolute',
              bottom: '0.5rem',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.75rem',
              fontVariantNumeric: 'tabular-nums',
              opacity: 0.4,
              color: theme.subText,
              pointerEvents: 'none',
              userSelect: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {pageIndex + 1} / {pageCount} · <FolioClock startedAt={startedAt} />
          </div>
        )}

        {/* Page announcements for screen readers (§4.5) — separate from the folio
            so the 1s clock tick never hits the live region. */}
        <div className="sr-only" aria-live="polite">
          Page {pageIndex + 1} of {pageCount}
        </div>
      </div>
    </>
  );
}
